import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/cn.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'icon' | 'urgent' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const variantClasses: Record<ButtonVariant, string> = {
  // Style guide §Buttons: pill, Poppins 600, primary in brand-blauw met dark-hover.
  primary:
    'bg-brand-primary text-text-on-dark hover:bg-brand-primary-dark active:bg-brand-primary-dark shadow-sm',
  secondary:
    'bg-surface text-text-secondary border border-border hover:bg-background hover:text-text-primary',
  ghost: 'bg-transparent text-brand-primary hover:bg-brand-primary-tint',
  icon: 'bg-transparent text-text-secondary hover:bg-background hover:text-brand-primary rounded-pill p-0',
  urgent: 'bg-brand-orange text-text-on-dark hover:bg-brand-orange-deep shadow-sm',
  /*
    Voor wat je niet met een klik terugdraait: verwijderen, archiveren, uit dienst zetten.

    Rood en niet oranje: oranje betekent in deze interface "vraagt aandacht" (dringend, nog te
    bevestigen). Zou verwijderen dezelfde kleur krijgen, dan zou een scherm met drie oranje knoppen
    de gevaarlijke niet meer laten opvallen.
  */
  danger: 'bg-danger text-text-on-dark hover:bg-danger-deep shadow-sm',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-small',
  md: 'h-10 px-4 text-content',
  lg: 'h-12 px-6 text-title-md',
};

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Toegankelijk label; verplicht voor de icon-variant, die geen zichtbare tekst heeft. */
  label?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', loading, disabled, children, label, ...props },
  ref,
) {
  const isIcon = variant === 'icon';
  return (
    <button
      ref={ref}
      type={props.type ?? 'button'}
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      data-touch-target=""
      className={cn(
        'inline-flex items-center justify-center gap-inline rounded-pill font-heading font-semibold',
        'transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50',
        isIcon ? iconSizeClasses[size] : sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
      {children}
    </button>
  );
});
